import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/app';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      const serverCode = (err as Record<string, unknown>).serverError
        ? ((err as Record<string, unknown>).serverError as Record<string, unknown>).code as string
        : null;

      if (message === 'NEW_PASSWORD_REQUIRED') {
        setError('Password change required. Contact your administrator.');
      } else if (serverCode === 'IDENTITY_NOT_FOUND' || message.includes('IDENTITY_NOT_FOUND')) {
        setError('Account not found in the database. The Cognito user may not have a matching Identity record.');
      } else if (serverCode === 'IDENTITY_INACTIVE' || message.includes('IDENTITY_INACTIVE')) {
        setError('Account is inactive. Contact your administrator.');
      } else if (serverCode === 'INTERNAL_ERROR') {
        setError('Server error during login. Check Lambda logs for details.');
      } else if (message.includes('Network Error')) {
        setError('Unable to reach the API. Check your network connection.');
      } else {
        setError('Invalid username or password.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/cct_background.png')" }}>
      <div className="w-full max-w-sm rounded-xl border border-white/10 bg-white/95 p-8 shadow-2xl backdrop-blur-sm">
        <div className="mb-6 flex flex-col items-center gap-3">
          <img src="/cct_circle_14.png" alt="CCT" className="h-16 w-16" />
          <h1 className="font-barlow text-2xl font-bold uppercase tracking-wide text-primary">
            Insight Cash
          </h1>
          <p className="text-sm text-muted-foreground">Sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="rounded border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="username" className="text-xs font-medium text-muted-foreground">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              className="rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-xs font-medium text-muted-foreground">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded bg-primary py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
